import { View, Text, Pressable, TextInput } from 'react-native';
import { useEffect, useState } from 'react';
import { usePathname, useRouter } from 'expo-router';
import { trpc } from '../../lib/trpc';
import { trackOnboardingStepCompleted } from '../../lib/product-events';
import { OnboardingShell, Button, Card, LifeEventForm, LifeEventList, WebHead } from '../../components';
import { formatCurrency } from '../../lib/utils';
import {
  toCreateLifeEventMutationInput,
  toUpdateLifeEventMutationInput,
  type GoalEvent,
  type LifeEventFormValues,
} from '../../lib/life-events';

const RISK_OPTIONS = [
  {
    value: 'conservative',
    label: 'Conservative',
    pct: '95%',
    desc: "I need high confidence I'll never need income again once I retire",
  },
  {
    value: 'moderate',
    label: 'Moderate',
    pct: '85%',
    desc: "Comfortable accepting some uncertainty for an earlier retirement",
  },
  {
    value: 'aggressive',
    label: 'Aggressive',
    pct: '75%',
    desc: "Retire as early as possible; I can adapt if markets disappoint",
  },
] as const;

const RISK_THRESHOLDS = {
  conservative: 0.95,
  moderate: 0.85,
  aggressive: 0.75,
} as const;

function getThresholdForRiskTolerance(value: string) {
  return RISK_THRESHOLDS[value as keyof typeof RISK_THRESHOLDS] ?? RISK_THRESHOLDS.moderate;
}

function parseNonNegativeCurrencyInput(value: string) {
  const normalized = value.replace(/[$,\s]/g, '');
  if (!normalized) return 0;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

export default function Step7LifeEvents() {
  const router = useRouter();
  const pathname = usePathname();
  const advanceMutation = trpc.households.updateOnboardingStep.useMutation();
  const createEventMutation = trpc.fire.createEvent.useMutation();
  const updateEventMutation = trpc.fire.updateEvent.useMutation();
  const deleteEventMutation = trpc.fire.deleteEvent.useMutation();
  const applyTemplateMutation = trpc.fire.applyEventTemplate.useMutation();
  const createPlanMutation = trpc.fire.createPlan.useMutation();
  const updatePlanMutation = trpc.fire.updatePlan.useMutation();
  const updateProfileMutation = trpc.profiles.update.useMutation();
  const planQ = trpc.fire.getPlan.useQuery();
  const eventsQ = trpc.fire.listEvents.useQuery();
  const profilesQ = trpc.profiles.list.useQuery();
  const suggestionsQ = trpc.fire.getSuggestedEvents.useQuery();
  const currentYear = new Date().getFullYear();

  const [adding, setAdding] = useState(false);
  const [hydratedProfileId, setHydratedProfileId] = useState<string | null>(null);
  const [hydratedPlanId, setHydratedPlanId] = useState<string | null>('__pending__');
  const [lifeExpectancy, setLifeExpectancy] = useState('90');
  const [riskTolerance, setRiskTolerance] = useState<string>('moderate');
  const [desiredEstateValue, setDesiredEstateValue] = useState('0');
  const [error, setError] = useState<string | null>(null);

  const events = (eventsQ.data ?? []) as GoalEvent[];
  const profiles = profilesQ.data ?? [];
  const primaryProfile = profiles.find((profile) => profile.type === 'primary') ?? profiles[0];
  const defaultPlanEndYear = planQ.data?.endYear ?? currentYear + 30;
  const currentFireYear = planQ.data?.beginYear ?? null;
  const unappliedSuggestions = (suggestionsQ.data ?? []).filter((s) => !s.isApplied);

  useEffect(() => {
    if (!primaryProfile || hydratedProfileId === primaryProfile.id) return;
    setLifeExpectancy(primaryProfile.lifeExpectancy?.toString() ?? '90');
    setRiskTolerance(primaryProfile.riskTolerance ?? 'moderate');
    setHydratedProfileId(primaryProfile.id);
  }, [hydratedProfileId, primaryProfile]);

  useEffect(() => {
    const plan = planQ.data;
    const planId = plan?.id ?? '__none__';
    if (hydratedPlanId === planId) return;
    setDesiredEstateValue(
      plan?.desiredEstateValue != null ? String(Math.round(Number(plan.desiredEstateValue))) : '0',
    );
    setHydratedPlanId(planId);
  }, [hydratedPlanId, planQ.data]);

  const handleApplySuggestion = async (templateGroupId: string) => {
    await applyTemplateMutation.mutateAsync({ templateGroupId });
    await eventsQ.refetch();
    suggestionsQ.refetch();
  };

  const handleAddEvent = async (values: LifeEventFormValues) => {
    await createEventMutation.mutateAsync(toCreateLifeEventMutationInput(values));
    await eventsQ.refetch();
    setAdding(false);
  };

  const handleUpdateEvent = async (id: string, values: LifeEventFormValues) => {
    await updateEventMutation.mutateAsync(toUpdateLifeEventMutationInput(id, values));
    await eventsQ.refetch();
  };

  const handleDeleteEvent = async (id: string) => {
    await deleteEventMutation.mutateAsync({ id });
    await eventsQ.refetch();
  };

  const handleContinue = async () => {
    setError(null);
    if (!primaryProfile) {
      setError('Could not find your profile. Try refreshing the page.');
      return;
    }

    const parsedDesiredEstateValue = parseNonNegativeCurrencyInput(desiredEstateValue);
    if (parsedDesiredEstateValue == null) {
      setError('Please enter a valid non-negative end-of-life net worth target.');
      return;
    }

    const threshold = getThresholdForRiskTolerance(riskTolerance);

    try {
      await updateProfileMutation.mutateAsync({
        id: primaryProfile.id,
        lifeExpectancy: parseInt(lifeExpectancy, 10) || 90,
        riskTolerance: riskTolerance as 'conservative' | 'moderate' | 'aggressive',
      });
      if (planQ.data?.id) {
        await updatePlanMutation.mutateAsync({
          id: planQ.data.id,
          threshold,
          desiredEstateValue: parsedDesiredEstateValue,
        });
      } else {
        await createPlanMutation.mutateAsync({
          threshold,
          desiredEstateValue: parsedDesiredEstateValue,
        });
      }
      await advanceMutation.mutateAsync({ step: 8 });
      trackOnboardingStepCompleted({
        route: pathname,
        stepNumber: 7,
        nextStepNumber: 8,
        completionKind: 'continue',
        metadata: {
          eventCount: events.length,
          didSkipEvents: events.length === 0,
          riskTolerance,
          thresholdPct: Math.round(threshold * 100),
          desiredEstateValue: Math.round(parsedDesiredEstateValue),
        },
      });
      router.replace('/(onboarding)/step-8-calculating' as any);
    } catch {
      setError('Could not save your settings. Please try again.');
    }
  };

  return (
    <OnboardingShell
      step={6}
      title="Life Events & FIRE Assumptions"
      subtitle="Add major life events and choose the assumptions Per Diem should use for your first FIRE projection."
      footer={
        <View className="gap-3">
          {error && (
            <View className="rounded-xl bg-red-50 px-4 py-3">
              <Text className="text-sm text-red-600">{error}</Text>
            </View>
          )}
          <Button
            testID="onboarding-goals-continue-button"
            accessibilityLabel="Save life events and calculate FIRE plan"
            onPress={handleContinue}
            loading={
              updateProfileMutation.isPending
              || createPlanMutation.isPending
              || updatePlanMutation.isPending
              || advanceMutation.isPending
            }
          >
            Save & Calculate →
          </Button>
        </View>
      }
    >
      <WebHead><title>Step 7: Life Events &amp; FIRE Assumptions – Per Diem</title></WebHead>
      <View className="gap-4">
        {unappliedSuggestions.length > 0 && (
          <Card>
            <Text className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">
              Smart Suggestions
            </Text>
            <Text className="text-xs text-gray-400 mb-4">
              Based on your family and retirement plans — add any that apply.
            </Text>
            {unappliedSuggestions.map((s) => (
              <View
                key={s.templateGroupId}
                className="flex-row items-center gap-3 py-2 border-b border-gray-50 dark:border-gray-700 last:border-0"
              >
                <View className="flex-1">
                  <Text className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    {s.label}
                  </Text>
                  <Text className="text-xs text-gray-400">{s.description}</Text>
                  <Text className="text-xs text-gray-400 mt-0.5">
                    {s.estimatedAnnualCost > 0
                      ? `~${formatCurrency(s.estimatedAnnualCost, { compact: true })}/yr`
                      : `${formatCurrency(s.estimatedOneTimeCost, { compact: true })} one-time`}
                  </Text>
                </View>
                <Button
                  size="sm"
                  variant="secondary"
                  onPress={() => handleApplySuggestion(s.templateGroupId)}
                  loading={applyTemplateMutation.isPending}
                >
                  Add
                </Button>
              </View>
            ))}
          </Card>
        )}

        <Card>
          {adding ? (
            <LifeEventForm
              profiles={profiles}
              currentFireYear={currentFireYear}
              defaultPlanEndYear={defaultPlanEndYear}
              onSave={handleAddEvent}
              onCancel={() => setAdding(false)}
              saving={createEventMutation.isPending}
            />
          ) : (
            <Button
              testID="life-event-add-button"
              accessibilityLabel="Add a life event"
              variant="secondary"
              onPress={() => setAdding(true)}
            >
              + Add a life event
            </Button>
          )}
        </Card>

        {events.length > 0 && (
          <Card>
            <Text className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
              Your Events
            </Text>
            <LifeEventList
              events={events}
              profiles={profiles}
              currentFireYear={currentFireYear}
              defaultPlanEndYear={defaultPlanEndYear}
              onUpdate={handleUpdateEvent}
              onDelete={handleDeleteEvent}
            />
          </Card>
        )}

        <Card>
          <Text className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">Life Expectancy</Text>
          <Text className="text-xs text-gray-400 mb-3">
            How long Per Diem plans your portfolio to last. Conservative planners use 95+.
          </Text>
            <TextInput
              testID="onboarding-goals-life-expectancy-input"
              accessibilityLabel="Life expectancy"
              value={lifeExpectancy}
            onChangeText={(text) => { setLifeExpectancy(text); setError(null); }}
            placeholder="90"
            keyboardType="numeric"
            className="border border-gray-200 dark:border-gray-600 rounded-xl px-4 py-3 text-base text-gray-800 dark:text-gray-200"
          />
        </Card>

        <Card>
          <Text className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">Retirement Confidence</Text>
          <Text className="text-xs text-gray-400 mb-4">
            How confident do you need to be that you'll never run out of money once you retire?
          </Text>
          <View className="gap-2">
            {RISK_OPTIONS.map((opt) => (
              <Pressable
                testID={`onboarding-goals-risk-${opt.value}-button`}
                accessibilityLabel={`Select ${opt.label} retirement confidence`}
                key={opt.value}
                className={`rounded-xl border px-4 py-3 ${
                  riskTolerance === opt.value
                    ? 'border-green-600 bg-green-50 dark:bg-green-900/30'
                    : 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700'
                }`}
                onPress={() => {
                  setRiskTolerance(opt.value);
                  setError(null);
                }}
              >
                <View className="flex-row items-baseline justify-between gap-3">
                  <Text
                    className={`text-sm font-semibold ${
                      riskTolerance === opt.value ? 'text-green-800 dark:text-green-300' : 'text-gray-800 dark:text-gray-200'
                    }`}
                  >
                    {opt.label}
                  </Text>
                  <Text
                    className={`text-base font-bold ${
                      riskTolerance === opt.value ? 'text-green-700' : 'text-gray-900'
                    }`}
                  >
                    {opt.pct}
                  </Text>
                </View>
                <Text
                  className={`mt-1 text-xs ${
                    riskTolerance === opt.value ? 'text-green-700/80' : 'text-gray-500'
                  }`}
                >
                  {opt.desc}
                </Text>
              </Pressable>
            ))}
          </View>
        </Card>

        <Card>
          <Text className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">Target Final Estate Value</Text>
          <Text className="text-xs text-gray-400 mb-4">
            Optional legacy goal for what you want left at the end of the plan. Leave it at 0 if you do not have a target.
          </Text>
          <View className="flex-row items-center border border-gray-200 dark:border-gray-600 rounded-xl px-4 py-3">
            <Text className="text-base text-gray-400 mr-2">$</Text>
            <TextInput
              testID="onboarding-goals-estate-value-input"
              accessibilityLabel="Target final estate value"
              value={desiredEstateValue}
              onChangeText={(text) => { setDesiredEstateValue(text); setError(null); }}
              placeholder="0"
              keyboardType="numeric"
              className="flex-1 text-base text-gray-800 dark:text-gray-200"
            />
          </View>
        </Card>
      </View>
    </OnboardingShell>
  );
}
